import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { Link } from "react-router-dom";
import Container from "@material-ui/core/Container";
import Alert from "@material-ui/lab/Alert";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";
import {withRouter} from "react-router-dom";
import actions from "../redux/actions";
import firebase, {cloudFunctions, firestore} from "../firebase";

const validationSchema = yup.object({
  email: yup
    .string("Enter your email")
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup.string("Enter your password").required("Required field"),
});

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  backdrop: {
    zIndex: 10000,
  },
  googleButton: {
    margin: theme.spacing(3, 0, 2),
  },
  googleIcon: {
    height: 20,
  },
  alertSpan: {
    cursor: "pointer"
  }
}));

function SignIn({history}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [error, setError] = useState(false);

  const { loginError = false, loading = false, showRenewMessage = false } = useSelector((state) => ({
    loginError: state.loginError,
    loading: state.loading,
    showRenewMessage: state.showRenewMessage
  }));
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      dispatch({
        type: actions.AUTH_USER,
        payload: {...values, history},
      });
    },
  });

  const setLoading = (v) => {
    dispatch({
      type: actions.SET_STATE,
      payload: {
        loading: v
      },
    });
  }

  const checkIfUserExists = async (uid) => {
    const doc = await firestore.collection('users').doc(uid).get();
    if (!doc.exists) {
      return {notExists: true};
    } else {
      const data = doc.data()
      if(data.disabled){
        return data
      } else {
        window.location.replace("")
        await delay(1000)
        return true;
      }
    }
  }

  function delay(delayInms) {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(2);
      }, delayInms);
    });
  }

  const registerWithGoogle = () => {
    setLoading(true)
    const googleProvider = new firebase.auth.GoogleAuthProvider();
    firebase
      .auth()
      .signInWithPopup(googleProvider)
      .then(async (res) => {
        const usr = res.user;
        const userExists = await checkIfUserExists(usr.email) ;
        if(userExists.notExists){
          const usr = res.user;
          const httpsCallable = cloudFunctions.httpsCallable('createUserGoogle');
          httpsCallable({fullName: usr.displayName, email: usr.email, uid: usr.email, isGoogle: true})
          .then((res)=>{
            setLoading(false);
            window.location.reload()
          }).catch((error) => {
            setLoading(false)
            setError(error.message ? error.message : "Error Occured. Please try later");
          });
        } else {
          dispatch({
            type: actions.SET_STATE,
            payload: {
              newUser: {fullName: usr.displayName, email: usr.email, uid: usr.email, isGoogle: true},
            }
          })
          setLoading(false)
          history.push("/plans")
        }
      })
      .catch((error) => {
        setLoading(false);
        if(error.code === "auth/user-disabled"){
          setError("Your subscription is expired, please renew first")
        }else{
          setError(error.message);
        }
        
      });
  };
  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Backdrop open={loading} className={classes.backdrop}>
        <CircularProgress style={{ width: 30, height: 30 }} color="inherit" />
      </Backdrop>
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Sign in
        </Typography>
        {loginError && <Alert severity="error">{loginError}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {showRenewMessage && <Alert severity="warning">Your subscription is expired, <span className={classes.alertSpan} onClick={() => history.push("/plans")}>click here to renew it</span></Alert>}
        <form className={classes.form} onSubmit={formik.handleSubmit}>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={formik.values.fullName}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            autoFocus
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
          >
            Sign In
          </Button>
          <Button
            onClick={registerWithGoogle}
            variant="contained"
            color="default"
            fullWidth
            className={classes.googleButton}
            startIcon={
              <img
                src="https://img.icons8.com/color/50/4a90e2/google-logo.png"
                alt="google icon"
                className={classes.googleIcon}
              />
            }
          >
            Login With Google
          </Button>
          <Grid container>
            <Grid item xs>
              <Link to="/reset" variant="body2">
                Forgot password?
              </Link>
            </Grid>
            <Grid item>
              <Link to="/signup" variant="body2">
                {"Don't have an account? Sign Up"}
              </Link>
            </Grid>
          </Grid>
        </form>
      </div>
    </Container>
  );
}

export default withRouter(SignIn)